"use client";
import React, { useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomDateInput from "./CustomDateInput";
import { dateFromDay } from "./calendarDay";
import { useLocationDay } from "./useLocationDay";
import {
  DEFAULT_DATE_FORMAT,
  addDaysToDbDate,
  getCustomerBookingHorizonNote,
  normalizeCustomerBookingHorizonDays,
} from "@utils/core";
import Text from "antd/es/typography/Text";
import {
  bookingSectionTitleStyle,
  bookingSmallStyle,
} from "../../bookingStyles";

interface DateSelectorProps {
  selectedDate: Date;
  setDate: (date: Date) => void;
  hasBorder?: boolean;
  filteredWorkDays: number[];
  excludedDates: Date[];
  showCalendar?: boolean;
  /** Optional section heading rendered above the date picker. */
  label?: string;
  /** Add a concise calendar date to the input so it is clear without a heading. */
  descriptiveInputLabel?: boolean;
  /**
   * Standalone grayscale calendar. A booking skin may restyle it through the
   * `--openings-booking-calendar-skin-*` vars; without them (every flat
   * preset) it renders the original grayscale values.
   */
  neutralPalette?: boolean;
  /**
   * The business's customer booking window (`customerBookingHorizonDays`).
   * When set, the picker stops at the last bookable day and says why. The
   * API enforces the same cut, so this only keeps customers off empty days.
   */
  bookingHorizonDays?: number | null;
  /**
   * The location's timezone (`bookingTimeZoneFor`). Today, its Today/Tomorrow
   * label, the earliest pickable day and the window's last day are counted
   * in it, the same day the openings API reads `date` in (BF-DEC-015).
   * Without it they fall back to the browser's day.
   */
  timeZone?: string | null;
}

/**
 * Stable popper container — must live outside the component so the reference
 * doesn't change on every render (which would unmount/remount the portal and
 * close the calendar immediately).
 */
const PopperContainer = ({ children }: { children?: React.ReactNode }) => {
  if (typeof document === "undefined" || children == null) {
    return <>{children}</>;
  }

  return createPortal(
    <div
      className="openings-booking-portal openings-booking-calendar-portal"
      style={{ display: "contents" }}
    >
      {children}
    </div>,
    document.body,
  );
};

const mobileCalendarViewportShift: NonNullable<
  React.ComponentProps<typeof DatePicker>["popperModifiers"]
>[number] = {
  name: "openings-mobile-calendar-viewport-shift",
  fn({ x, rects }) {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      typeof window.matchMedia !== "function" ||
      !window.matchMedia("(max-width: 767px)").matches
    ) {
      return {};
    }

    const viewportWidth = document.documentElement.clientWidth;
    const gutter = 16;
    const maximumX = Math.max(
      gutter,
      viewportWidth - rects.floating.width - gutter,
    );
    const centeredX =
      x + (rects.reference.width - rects.floating.width) / 2;

    return {
      x: Math.min(Math.max(centeredX, gutter), maximumX),
    };
  },
};

const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  setDate,
  hasBorder,
  filteredWorkDays,
  excludedDates,
  showCalendar,
  label,
  descriptiveInputLabel = false,
  neutralPalette = false,
  bookingHorizonDays,
  timeZone,
}) => {
  const horizonDays = normalizeCustomerBookingHorizonDays(bookingHorizonDays);
  // Today is the location's (BF-DEC-015), read from the clock: null until
  // hydration, and refreshed when the location's day turns over even if the
  // visitor's has not. The window ends N days after it.
  const locationDay = useLocationDay(timeZone);
  const today = useMemo(
    () => (locationDay ? dateFromDay(locationDay) : null),
    [locationDay],
  );
  const maxDate = useMemo(() => {
    if (horizonDays === null || !locationDay) return null;
    return dateFromDay(addDaysToDbDate(locationDay, horizonDays));
  }, [locationDay, horizonDays]);
  const horizonNote = getCustomerBookingHorizonNote(bookingHorizonDays);
  const isLastBookableDay =
    maxDate !== null && !dayjs(selectedDate).startOf("day").isBefore(maxDate);
  const dateMoment = dayjs(selectedDate);
  const dayDifference = today
    ? dateMoment.startOf("day").diff(dayjs(today), "day")
    : null;
  const descriptiveLabel =
    dayDifference === null
      ? ""
      : dayDifference === 0
        ? `Today · ${dateMoment.format("MMM D")}`
        : dayDifference === 1
          ? `Tomorrow · ${dateMoment.format("MMM D")}`
          : dateMoment.format("ddd · MMM D");
  const inputRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {label && <Text style={bookingSectionTitleStyle}>{label}</Text>}
      <style>
        {`
          .react-datepicker-wrapper {
            width: 100%;
          }
          .react-datepicker-popper {
            position: absolute !important;
            z-index: 10001;
          }
          .openings-booking-calendar-popper--neutral,
          .openings-booking-calendar--neutral {
            --openings-booking-calendar-surface: var(--openings-booking-calendar-skin-surface, #fff);
            --openings-booking-calendar-text: var(--openings-booking-calendar-skin-text, #111);
            --openings-booking-calendar-text-secondary: var(--openings-booking-calendar-skin-text-secondary, #666);
            --openings-booking-calendar-border: var(--openings-booking-calendar-skin-border, #e5e5e5);
            --openings-booking-calendar-header: var(--openings-booking-calendar-skin-header, #f7f7f7);
            --openings-booking-calendar-hover: var(--openings-booking-calendar-skin-hover, #f2f2f2);
            --openings-booking-calendar-selected-background: var(--openings-booking-calendar-skin-selected-background, #111);
            --openings-booking-calendar-selected-text: var(--openings-booking-calendar-skin-selected-text, #fff);
            --openings-booking-calendar-disabled: var(--openings-booking-calendar-skin-disabled, #aaa);
          }
          .openings-booking-calendar--neutral {
            background: var(--openings-booking-calendar-surface);
            border-color: var(--openings-booking-calendar-border);
            border-radius: var(--openings-booking-calendar-skin-radius, 0.3rem);
            box-shadow: var(--openings-booking-calendar-skin-shadow, none);
            color: var(--openings-booking-calendar-text);
            font-family: var(--openings-booking-calendar-skin-font-family, inherit);
          }
          .openings-booking-calendar-popper--neutral
            .react-datepicker__triangle {
            stroke: var(--openings-booking-calendar-skin-border, #aeaeae);
          }
          .openings-booking-calendar-popper--neutral[data-placement^="bottom"]
            .react-datepicker__triangle {
            fill: var(--openings-booking-calendar-skin-header, #f0f0f0);
            color: var(--openings-booking-calendar-skin-header, #f0f0f0);
          }
          .openings-booking-calendar-popper--neutral[data-placement^="top"]
            .react-datepicker__triangle {
            fill: var(--openings-booking-calendar-skin-surface, #fff);
            color: var(--openings-booking-calendar-skin-surface, #fff);
          }
          .openings-booking-calendar--neutral .react-datepicker__header {
            border-bottom-color: var(--openings-booking-calendar-border);
            border-top-left-radius: var(--openings-booking-calendar-skin-radius, 0.3rem);
            border-top-right-radius: var(--openings-booking-calendar-skin-radius, 0.3rem);
            background: var(--openings-booking-calendar-header);
          }
          .openings-booking-calendar--neutral
            .react-datepicker__current-month,
          .openings-booking-calendar--neutral
            .react-datepicker__day-name,
          .openings-booking-calendar--neutral .react-datepicker__day {
            color: var(--openings-booking-calendar-text);
          }
          .openings-booking-calendar--neutral
            .react-datepicker__day:hover,
          .openings-booking-calendar--neutral
            .react-datepicker__day--keyboard-selected:not(
              .react-datepicker__day--selected
            ) {
            background: var(--openings-booking-calendar-hover);
            color: var(--openings-booking-calendar-text);
          }
          .openings-booking-calendar--neutral
            .react-datepicker__day--selected,
          .openings-booking-calendar--neutral
            .react-datepicker__day--selected:hover {
            background: var(--openings-booking-calendar-selected-background);
            color: var(--openings-booking-calendar-selected-text);
          }
          .openings-booking-calendar--neutral
            .react-datepicker__day:focus-visible {
            outline: 2px solid var(--openings-booking-calendar-selected-background);
            outline-offset: 1px;
          }
          .openings-booking-calendar--neutral
            .react-datepicker__day--disabled {
            color: var(--openings-booking-calendar-disabled);
          }
          .openings-booking-calendar--neutral
            .react-datepicker__navigation-icon::before {
            border-color: var(--openings-booking-calendar-text-secondary);
          }
          @media (max-width: 767px) {
            .openings-booking-calendar-popper--neutral
              .react-datepicker__triangle {
              display: none;
            }
            .openings-booking-calendar--neutral {
              box-sizing: border-box;
              width: min(320px, calc(100vw - 32px));
              overflow: hidden;
              border-radius: var(--openings-booking-calendar-skin-radius, 10px);
              font-size: 14px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__month-container {
              float: none;
              width: 100%;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__header {
              padding: 13px 48px;
              border-radius:
                var(--openings-booking-calendar-skin-radius, 10px)
                var(--openings-booking-calendar-skin-radius, 10px)
                0
                0;
              background: var(--openings-booking-calendar-surface);
            }
            .openings-booking-calendar--neutral
              .react-datepicker__current-month {
              font-size: 16px;
              font-weight: 600;
              line-height: 22px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation {
              top: 4px;
              width: 40px;
              height: 40px;
              border-radius: var(--openings-booking-calendar-skin-control-radius, 8px);
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation--previous {
              left: 4px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation--next {
              right: 4px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation:hover {
              background: var(--openings-booking-calendar-hover);
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation:focus-visible {
              outline: 2px solid var(--openings-booking-calendar-text);
              outline-offset: -2px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation-icon {
              top: 0;
              width: 8px;
              height: 8px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation-icon--next {
              left: -1px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation-icon--previous {
              right: -1px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__navigation-icon::before {
              inset: 0;
              width: 8px;
              height: 8px;
              border-width: 2px 2px 0 0;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__day-names,
            .openings-booking-calendar--neutral
              .react-datepicker__week {
              display: grid;
              grid-template-columns: repeat(7, minmax(0, 1fr));
              white-space: normal;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__day-names {
              margin: 4px 3px 0;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__month {
              margin: 0 3px 8px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__day-name {
              width: auto;
              margin: 0;
              color: var(--openings-booking-calendar-text-secondary);
              font-size: 13px;
              font-weight: 500;
              line-height: 32px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__day {
              box-sizing: border-box;
              width: 100%;
              max-width: 40px;
              height: 40px;
              margin: 0 auto;
              border-radius: var(--openings-booking-calendar-skin-control-radius, 8px);
              font-size: 14px;
              line-height: 40px;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__day--disabled:hover {
              background: transparent;
            }
            .openings-booking-calendar--neutral
              .react-datepicker__day:focus-visible {
              outline-offset: -2px;
            }
          }
        `}
      </style>
      <DatePicker
        selected={selectedDate}
        minDate={today ?? undefined}
        maxDate={maxDate ?? undefined}
        onChange={(date: Date | null) => {
          if (date) {
            setDate(date);
          }
        }}
        excludeDates={excludedDates}
        customInput={
          <CustomDateInput
            ref={inputRef}
            value={dateMoment.format(DEFAULT_DATE_FORMAT)}
            selectedDate={selectedDate}
            today={today}
            onClick={() => inputRef.current?.focus()}
            onNext={() => setDate(dateMoment.add(1, "day").toDate())}
            onPrev={() => setDate(dateMoment.subtract(1, "day").toDate())}
            hasBorder={hasBorder}
            displayLabel={
              descriptiveInputLabel ? descriptiveLabel : undefined
            }
            nextDisabled={isLastBookableDay}
          />
        }
        filterDate={(date: Date) => !filteredWorkDays.includes(date.getDay())}
        inline={showCalendar}
        calendarClassName={
          neutralPalette
            ? "openings-booking-calendar openings-booking-calendar--neutral"
            : undefined
        }
        popperClassName={
          neutralPalette
            ? "openings-booking-calendar-popper--neutral"
            : undefined
        }
        popperPlacement="bottom-start"
        popperModifiers={
          neutralPalette ? [mobileCalendarViewportShift] : []
        }
        popperContainer={PopperContainer as any}
      />
      {horizonNote && (
        <Text style={{ ...bookingSmallStyle, display: "block", marginTop: 6 }}>
          {horizonNote}
        </Text>
      )}
    </>
  );
};

export default DateSelector;
